/**
 * Preloads a font and returns a Promise that resolves when the font is loaded
 * @param {string} fontFamily - The font family name
 * @param {string} fontUrl - The URL to the font file
 * @param {string} fontWeight - The font weight (default: 'normal')
 * @returns {Promise} - Promise that resolves when font is loaded or rejects on error
 */
const preloadFont = (fontFamily, fontUrl, fontWeight = 'normal') => {
    return new Promise((resolve, reject) => {
        // Check if Font Loading API is available (modern browsers)
        if ('fonts' in document) {
            const font = new FontFace(fontFamily, `url(${fontUrl})`, { weight: fontWeight });
            font.load()
                .then(() => {
                    document.fonts.add(font);
                    resolve();
                })
                .catch(reject);
        } else {
            // Fallback for older browsers - create a test element and wait
            const testElement = document.createElement('span');
            testElement.style.fontFamily = fontFamily;
            testElement.style.visibility = 'hidden';
            testElement.style.position = 'absolute';
            testElement.style.top = '-9999px';
            testElement.textContent = 'Test';
            document.body.appendChild(testElement);

            // Wait a reasonable time for font to load
            setTimeout(() => {
                document.body.removeChild(testElement);
                resolve();
            }, 2000);
        }
    }).catch(err => {
        console.error(`Failed to preload font: ${fontFamily}`, err);
        return null;
    });
};

/**
 * Preloads multiple fonts and returns a Promise that resolves when all fonts are loaded
 * @param {Array} fonts - Array of font objects with family, url, and optional weight properties
 * @returns {Promise} - Promise that resolves when all fonts are loaded
 */
const preloadFonts = async (fonts) => {
    const promises = [];

    for (const font of fonts) {
        if (font.family && font.url) {
            // Load both normal and bold weights for each font
            promises.push(preloadFont(font.family, font.url, 'normal'));
            promises.push(preloadFont(font.family, font.url, 'bold'));
        }
    }

    await Promise.all(promises);
};

export { preloadFont, preloadFonts }; 